import { useCallback } from 'react';

import {
  CopyIcon,
  EllipsisVerticalIcon,
  MessageCircleIcon,
  PencilIcon,
  Trash2Icon,
  UploadIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';

import { BRAIN_DELETE, MODAL_TRYIT } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import useBrains from '@/hooks/useBrains';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { setBrainId } from '@/redux/session/actions';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { trackEvent } from '@/segment/segment';
import { AIAgent } from '@/shared/types/brain';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';

interface ActionsCellProps {
  item: AIAgent;
}

const ActionsCell = ({ item }: ActionsCellProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { exportBrain } = useBrains();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.DELETE)
  );

  const handleTest = useCallback(() => {
    trackEvent(EventName.ClickTryIt, { brain_id: item.brain_id });
    dispatch(pushModal(MODAL_TRYIT, { brainId: item.brain_id }));
  }, [dispatch, item.brain_id]);

  const handleRename = useCallback(() => {
    dispatch(setSidebarContent('edit_ai_agent'));
    dispatch(setBrainId({ brainId: item.brain_id }));
  }, [dispatch, item.brain_id]);

  const handleExport = useCallback(() => {
    trackEvent(EventName.ClickExportBrain, { brain_id: item.brain_id });
    exportBrain(item.brain_id);
  }, [exportBrain, item.brain_id]);

  const handleCopyId = useCallback(async () => {
    trackEvent(EventName.ClickCopyBrainId, { brain_id: item.brain_id });

    try {
      await navigator.clipboard.writeText(item.brain_id);
      toast.success(t('common.copied'));
    } catch {
      toast.error(t('common.error_copying_id'));
    }
  }, [item.brain_id, t]);

  const handleDelete = useCallback(() => {
    const warnProps = { brainName: item.name, brainId: item.brain_id };
    dispatch(pushModal(BRAIN_DELETE, warnProps));
  }, [dispatch, item.brain_id, item.name]);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          variant="ghost-neutral"
          size="icon-sm"
        >
          <EllipsisVerticalIcon size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <DropdownMenuItem onSelect={handleTest}>
          <MessageCircleIcon size={16} className="text-muted-foreground" />
          {`${t('common.test')}…`}
        </DropdownMenuItem>

        <DropdownMenuItem
          onSelect={handleRename}
          disabled={!canWrite}
          className="flex items-center gap-2"
        >
          <PencilIcon size={16} className="text-muted-foreground" />
          {`${t('common.rename')}…`}
        </DropdownMenuItem>

        <DropdownMenuItem
          onSelect={handleDelete}
          disabled={!canDelete}
          variant="destructive"
          className="flex items-center gap-2"
        >
          <Trash2Icon size={16} />
          {`${t('common.delete')}…`}
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={handleExport} className="flex items-center gap-2">
          <UploadIcon size={16} className="text-muted-foreground" />
          {t('common.export')}
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={handleCopyId} className="flex items-center gap-2">
          <CopyIcon size={16} className="text-muted-foreground" />
          {t('brains.copy_id')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ActionsCell;
